import React from "react";
import {
  Code2,
  Brain,
  Bot,
  Server,
  Database,
  Workflow,
} from "lucide-react";
import { skills } from "../mock";

const TEAL = "#1ea896";
const RADIUS = 38;
const ANGLES = [-90, -30, 30, 90, 150, 210];

const categoryMeta = [
  { title: "Languages & Web", icon: Code2 },
  { title: "Machine Learning & AI", icon: Brain },
  { title: "Agentic AI & LLMs", icon: Bot },
  { title: "Backend & APIs", icon: Server },
  { title: "Databases & Storage", icon: Database },
  { title: "Automation & Tools", icon: Workflow },
];

const categories = categoryMeta
  .map((meta) => ({ ...meta, list: skills[meta.title] || [] }))
  .filter((c) => c.list.length > 0);

const getPos = (angle) => {
  const rad = (angle * Math.PI) / 180;
  return {
    x: 50 + RADIUS * Math.cos(rad),
    y: 50 + RADIUS * Math.sin(rad),
  };
};

const CategoryCard = ({ meta, list }) => {
  const Icon = meta.icon;
  return (
    <div className="group/card cursor-target flex h-full flex-col rounded-2xl border border-[#1ea896]/20 bg-white/90 shadow-md shadow-[#1ea896]/5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#1ea896]/40 hover:shadow-[0_10px_30px_rgba(30,168,150,0.18)] dark:bg-[#101d1e]">
      <div className="flex items-center gap-3 border-b border-[#1ea896]/10 px-3.5 py-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#1ea896] text-white shadow-md shadow-[#1ea896]/30 transition-transform duration-300 group-hover/card:scale-110 group-hover/card:-rotate-6">
          <Icon size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-[13px] font-semibold leading-tight text-gray-900 dark:text-gray-100 sm:text-sm">
            {meta.title}
          </h3>
          <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-[#1ea896]/20 bg-[#1ea896]/10 px-2 py-px font-mono text-[9px] font-semibold text-[#1ea896]">
            <span className="h-1 w-1 rounded-full bg-[#1ea896]" />
            {list.length} technologies
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-wrap content-start gap-1.5 p-3">
        {list.map((skill) => (
          <span
            key={skill}
            className="rounded-full border border-[#1ea896]/25 bg-[#1ea896]/10 px-2 py-0.5 text-[10px] font-medium leading-tight text-gray-800 transition-all duration-300 hover:scale-105 hover:border-[#1ea896]/40 hover:bg-[#1ea896]/20 hover:shadow-[0_0_10px_rgba(30,168,150,0.45)] sm:text-[11px] dark:text-gray-200"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
};

const RadialSkills = () => {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-b from-white to-slate-50 dark:from-[#0d1718] dark:to-[#0a1213] p-3 sm:p-6 lg:p-8">
      {/* Mobile center hub */}
      <div className="mb-6 flex justify-center lg:hidden">
        <div className="relative flex items-center gap-3 rounded-full border border-[#1ea896]/25 bg-white py-2 pl-2 pr-5 shadow-lg shadow-[#1ea896]/10 dark:bg-[#101d1e]">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#1ea896] font-display text-sm font-bold text-white shadow-md shadow-[#1ea896]/30">
            AA
          </span>
          <div className="leading-tight">
            <span className="block font-display text-sm font-bold text-gray-900 dark:text-gray-50">
              Adeel Ahmed
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
              AI Engineer
            </span>
          </div>
        </div>
      </div>

      {/* Mobile / tablet: responsive grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:hidden">
        {categories.map((cat, i) => (
          <div
            key={cat.title}
            className="animate-pop-in h-full"
            style={{ animationDelay: `${i * 70}ms` }}
          >
            <CategoryCard meta={cat} list={cat.list} />
          </div>
        ))}
      </div>

      {/* Desktop: radial hub */}
      <div className="relative mx-auto hidden aspect-square w-full max-w-[min(96vw,60rem)] lg:block">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          aria-hidden="true"
        >
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke={TEAL}
            strokeOpacity="0.14"
            strokeWidth="0.35"
            strokeDasharray="1.2 1.6"
          />
        </svg>

        {/* Center hub */}
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <div className="relative grid h-44 w-44 place-items-center xl:h-52 xl:w-52">
            <div className="absolute inset-0 rounded-full bg-[#1ea896]/20 blur-2xl animate-hub-pulse" />
            <div className="absolute inset-0 rounded-full border border-[#1ea896]/40" />
            <div className="relative flex h-[92%] w-[92%] flex-col items-center justify-center rounded-full bg-white px-3 text-center shadow-xl shadow-[#1ea896]/15 dark:bg-[#101d1e]">
              <span className="font-display text-xl font-bold leading-tight tracking-tight text-gradient xl:text-2xl">
                AI Engineer
              </span>
            </div>
          </div>
        </div>

        {/* Category cards orbiting the hub */}
        {categories.map((cat, i) => {
          const p = getPos(ANGLES[i]);
          return (
            <div
              key={cat.title}
              className="animate-pop-in-center absolute z-10 w-[min(34%,20rem)] -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${p.x}%`, top: `${p.y}%`, animationDelay: `${i * 110}ms` }}
            >
              <CategoryCard meta={cat} list={cat.list} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RadialSkills;
